import { Loader2, Save, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../components/layout/AppLayout.js";
import { useAuth } from "../hooks/useAuth.js";
import { type AdminUser, type Role, adminUsersApi } from "../lib/api.js";

const ROLES: Role[] = ["admin", "hrbp", "manager", "viewer", "kpi_viewer", "vorstand"];

export function UserAdmin() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Partial<AdminUser>>>({});

  async function load() {
    try {
      const res = await adminUsersApi.list();
      setUsers(res.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function updateDraft(id: string, patch: Partial<AdminUser>) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  }

  async function save(u: AdminUser) {
    const draft = drafts[u.id];
    if (!draft) return;
    setSavingId(u.id);
    try {
      const res = await adminUsersApi.update(u.id, {
        role: draft.role,
        displayName: draft.displayName,
        assignedEmployeeIds: draft.assignedEmployeeIds,
        personioEmployeeId: draft.personioEmployeeId,
        disabled: draft.disabled,
      });
      setUsers((us) => us?.map((x) => (x.id === u.id ? res.user : x)) ?? null);
      setDrafts((d) => {
        const n = { ...d };
        delete n[u.id];
        return n;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  }

  async function toggleDisabled(u: AdminUser) {
    await adminUsersApi.update(u.id, { disabled: !u.disabled });
    await load();
  }

  async function remove(u: AdminUser) {
    if (!confirm(`User ${u.email} wirklich löschen?`)) return;
    await adminUsersApi.remove(u.id);
    await load();
  }

  const isAdmin = me?.role === "admin";

  return (
    <AppLayout>
      {/* F21 (26.08.2026): eigener Scroll-Bereich — AppLayout schneidet sonst ab. */}
      <div className="flex-1 overflow-y-auto w-full max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
            {t("admin.users.title", { defaultValue: "Benutzerverwaltung" })}
          </h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("admin.users.subtitle", {
              defaultValue:
                "Rollen, Personio-Zuordnung und Zugriff pflegen. Neue User werden beim ersten Microsoft-Login automatisch als Viewer angelegt.",
            })}
          </p>
        </div>

        {!isAdmin && (
          <div
            className="card p-4 mb-4"
            style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
          >
            Keine Admin-Rechte.
          </div>
        )}

        {error && (
          <div
            className="mb-4 px-4 py-3 rounded text-sm"
            style={{
              background: "var(--risk-high-bg)",
              color: "var(--risk-high-text)",
              border: "1px solid var(--risk-high)",
            }}
          >
            {error}
          </div>
        )}

        {!users ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <Loader2 size={14} className="animate-spin" /> Lade User…
          </div>
        ) : users.length === 0 ? (
          <div
            className="card p-8 text-center text-sm"
            style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
          >
            Noch keine User vorhanden. Microsoft-Login legt sie bei erster Anmeldung automatisch an.
          </div>
        ) : (
          <div className="card" style={{ borderColor: "var(--border)", overflow: "hidden" }}>
            <table className="w-full text-sm">
              <thead>
                <tr
                  style={{
                    background: "var(--bg-surface)",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    User
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Provider
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Rolle
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Personio-ID
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Zugeordnete MA
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Zuletzt
                  </th>
                  <th
                    className="text-right px-4 py-3 font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const draft = drafts[u.id];
                  const role = draft?.role ?? u.role;
                  const pid =
                    draft?.personioEmployeeId !== undefined
                      ? draft.personioEmployeeId
                      : u.personioEmployeeId;
                  const assigned = draft?.assignedEmployeeIds ?? u.assignedEmployeeIds;
                  const hasDraft = Boolean(draft);
                  return (
                    <tr
                      key={u.id}
                      style={{
                        borderBottom: "1px solid var(--border)",
                        opacity: u.disabled ? 0.5 : 1,
                      }}
                    >
                      <td className="px-4 py-3">
                        <div style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                          {u.displayName}
                        </div>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                          {u.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                        {u.provider}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={role}
                          disabled={!isAdmin}
                          onChange={(e) => updateDraft(u.id, { role: e.target.value as Role })}
                          className="px-2 py-1 rounded text-sm"
                          style={{
                            background: "var(--bg-hover)",
                            border: "1px solid var(--border)",
                            color: "var(--text-primary)",
                          }}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={pid ?? ""}
                          disabled={!isAdmin}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateDraft(u.id, { personioEmployeeId: v ? Number(v) : null });
                          }}
                          placeholder="—"
                          className="w-24 px-2 py-1 rounded text-sm"
                          style={{
                            background: "var(--bg-hover)",
                            border: "1px solid var(--border)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={assigned.join(", ")}
                          disabled={!isAdmin || role !== "hrbp"}
                          onChange={(e) => {
                            const ids = e.target.value
                              .split(",")
                              .map((s) => Number(s.trim()))
                              .filter((n) => Number.isFinite(n) && n > 0);
                            updateDraft(u.id, { assignedEmployeeIds: ids });
                          }}
                          placeholder={role === "hrbp" ? "IDs kommagetrennt" : "—"}
                          className="w-40 px-2 py-1 rounded text-sm"
                          style={{
                            background: "var(--bg-hover)",
                            border: "1px solid var(--border)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasDraft && (
                            <button
                              type="button"
                              onClick={() => save(u)}
                              disabled={savingId === u.id}
                              className="px-2 py-1 rounded text-xs flex items-center gap-1"
                              style={{ background: "var(--accent)", color: "#fff" }}
                            >
                              {savingId === u.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Save size={12} />
                              )}
                              Speichern
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleDisabled(u)}
                            disabled={!isAdmin}
                            title={u.disabled ? "Aktivieren" : "Deaktivieren"}
                            className="p-1.5 rounded"
                            style={{
                              background: "var(--bg-hover)",
                              color: u.disabled ? "var(--accent)" : "var(--text-muted)",
                            }}
                          >
                            {u.disabled ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(u)}
                            disabled={!isAdmin}
                            title="Löschen"
                            className="p-1.5 rounded"
                            style={{
                              background: "var(--bg-hover)",
                              color: "var(--risk-high-text)",
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-4 text-xs" style={{ color: "var(--text-muted)" }}>
          Hinweis: HRBP sieht firmenweit alle Mitarbeiterdaten (keine Linien-Einschränkung). Bei der
          Manager-Rolle steuert die Berichtslinie (Personio-Feld <code>managerId</code>) die
          Sichtbarkeit; manuelle Zuordnungen kommen on-top.
        </p>
      </div>
    </AppLayout>
  );
}
